import type { NudgeStatus, DeviceType } from './enums'

// ─── Auth / Session ──────────────────────────────────────────────────────────

export type OnboardingStatus = {
  onboardingCompleted: boolean
}

// ─── User ────────────────────────────────────────────────────────────────────

export type UserProfile = {
  id?: string | null
  email?: string | null
  firstName?: string | null
  lastName?: string | null
  phone?: string | null
  location?: string | null
  timezone?: string | null
  avatarUrl?: string | null
  createdAt?: string | null
  role?: string | null
  authProvider?: string | null
}

export type ProfileCompletion = {
  profileCompletionPercent?: number | null
}

export type SecuritySettings = {
  twoFactorEnabled: boolean
}

export type Device = {
  id: string
  deviceName?: string | null
  deviceType: DeviceType
  ipAddress?: string | null
  lastUsed?: string | null
}

export type DevicesResponse = {
  devices: Device[]
}

// ─── Business ────────────────────────────────────────────────────────────────

export type BusinessInfo = {
  businessName?: string | null
  businessType?: string | null
  phone?: string | null
  location?: string | null
  timezone?: string | null
  lapseWindowDays?: number | null
  dailyScanTime?: string | null
  emailGenerationInstructions?: string | null
  defaultOfferText?: string | null
  fromDisplayName?: string | null
  brandVoice?: string | null
  specialtySummary?: string | null
  bookingUrl?: string | null
  hoursSummary?: string | null
  websiteUrl?: string | null
  sendingEmail?: string | null
  onboardingCompleted?: boolean | null
  schedulerSlotId?: number | null
  followUpIntervalDays?: number | null
  mentionPrices?: boolean | null
  logoUrl?: string | null
}

// ─── Contacts ────────────────────────────────────────────────────────────────

export type Contact = {
  id: string
  fullName: string
  email: string
  phone?: string | null
  lastVisitDate: string
  metadata?: Record<string, string>
}

export type ImportResult = {
  importedCount: number
  skippedCount: number
  errors: string[]
}

export type OfferingType = 'SERVICE' | 'PRODUCT'

export type Service = {
  id: string
  name: string
  description?: string | null
  price?: number | null
  durationMinutes?: number | null
  active: boolean
  /** "SERVICE" (default) or "PRODUCT". Older rows may omit it. */
  offeringType?: OfferingType | null
  createdAt: string
}

export type ContactVisit = {
  id: string
  contactId: string
  serviceId?: string | null
  serviceName?: string | null
  visitDate: string
  staffName?: string | null
  totalAmount?: number | null
  notes?: string | null
}

// ─── Nudge Queue ─────────────────────────────────────────────────────────────

export type ConversationTurn = {
  // Backend may emit "assistant" (from the AI server) or legacy "business" for the
  // business side; both should render as the business / right-side bubble.
  role: 'business' | 'assistant' | 'customer'
  content: string
  timestamp: string
}

export type NudgeQueueSummary = {
  id: string
  contactId: string
  contactName: string
  contactEmail: string
  status: NudgeStatus
  sequenceNumber: number
  sentAt?: string | null
  replyReceivedAt?: string | null
  followUpSendAfter?: string | null
  createdAt: string
  updatedAt: string
  /** ISO date the customer committed to returning by, when extracted by the AI. */
  committedByDate?: string | null
  /** Most recent intent the AI classified for this conversation. */
  lastIntent?: string | null
}

export type NudgeQueueDetail = NudgeQueueSummary & {
  emailSubject?: string | null
  emailBody?: string | null
  conversationHistory: ConversationTurn[]
  failedStage?: string | null
  retryCount?: number | null
  recoveredAt?: string | null
}

export type NudgeMetrics = Partial<Record<NudgeStatus, number>>

// ─── Admin ────────────────────────────────────────────────────────────────────

export type SchedulerSlot = {
  slotId: string
  timezone: string
  slotCode: 'MORNING' | 'AFTERNOON' | 'EVENING'
  fireTimeLocal: string
  active: boolean
}

// ─── Billing ─────────────────────────────────────────────────────────────────

export type PlanCode =
  | 'FREE'
  // Legacy paid tiers — retained so existing subscriptions keep resolving;
  // hidden from /billing/plans by is_active=false on the seed rows.
  | 'STARTER'
  | 'GROWTH'
  | 'BUSINESS'
  // Current marketing catalog
  | 'PRO'        // Fixed 1,000 contacts
  | 'PRO_PLUS'   // Metered: $29 per 1,000-contact block
  | 'ENTERPRISE' // Admin-assigned only

export type BillingCycle = 'MONTHLY' | 'ANNUAL'

export type SubscriptionStatus =
  | 'INCOMPLETE'
  | 'TRIALING'
  | 'ACTIVE'
  | 'PAST_DUE'
  | 'PAUSED'
  | 'GRACE'
  | 'CANCELED'
  | 'EXPIRED'

export type FeatureKey =
  | 'NUDGE_REPLY_AI'
  | 'RECOVERY_ALERTS'
  | 'CONTACT_LIMIT'
  | 'CUSTOM_SLOT_TIMES'
  | 'OPT_OUT_ANALYTICS'
  | 'PRIORITY_QUEUE'
  | 'MAX_OWNER_USERS'

export type Plan = {
  id: string
  code: PlanCode
  displayName: string
  contactLimit: number
  monthlyCents: number
  annualCents: number
  currency: string
  trialDays: number
  // Metered pricing (PRO_PLUS). isMetered=false ⇒ unit fields are null.
  isMetered: boolean
  unitContactSize: number | null
  unitMonthlyCents: number | null
  unitAnnualCents: number | null
  minUnits: number | null
  maxUnits: number | null
}

export type EntitlementValue =
  | { type: 'BOOL'; value: boolean }
  | { type: 'INT'; value: number }

export type EntitlementsSnapshot = Partial<Record<FeatureKey, EntitlementValue>>

export type Subscription = {
  id: string
  planCode: PlanCode
  planDisplayName: string
  status: SubscriptionStatus
  billingCycle: BillingCycle
  currentPeriodStart: string
  currentPeriodEnd: string
  trialEnd: string | null
  cancelAtPeriodEnd: boolean
  canceledAt: string | null
  gracePeriodEndsAt: string | null
  amountCents: number
  currency: string
}

export type CurrentSubscriptionResponse = {
  subscription: Subscription | null
  plan: Plan
  entitlements: EntitlementsSnapshot
  usage: {
    contactsUsed: number
    contactLimit: number
  }
}

export type Invoice = {
  id: string
  amountCents: number
  taxCents: number
  totalCents: number
  currency: string
  status: 'OPEN' | 'PAID' | 'VOID' | 'UNCOLLECTIBLE' | 'REFUNDED'
  hostedInvoiceUrl: string | null
  periodStart: string
  periodEnd: string
  issuedAt: string
  paidAt: string | null
}

export type CheckoutSessionResponse = { checkoutUrl: string }
export type BillingPortalResponse   = { portalUrl: string }

export type ContactLimitErrorPayload = {
  feature: 'CONTACT_LIMIT'
  limit: number
  currentUsage: number
  upgradeUrl?: string
}

export type BillingMetrics = {
  mrrCents: number
  currency: string
  countsByStatus: Partial<Record<SubscriptionStatus, number>>
  topPlans: { planCode: PlanCode; count: number }[]
}

export type AdminSubscriptionRow = Subscription & {
  businessId: string
  businessName: string
  ownerEmail: string
}

export type AdminBusinessResult = {
  businessId: string
  businessName: string
  ownerEmail: string
  ownerName?: string | null
}

export type AdminBusinessRow = {
  businessId: string
  businessName: string
  businessType?: string | null
  ownerEmail: string
  ownerName?: string | null
  planCode: PlanCode
  planDisplayName: string
  subscriptionStatus: SubscriptionStatus | null
  contactCount: number
  createdAt: string
}

export type AdminUserRow = {
  userId: string
  email: string
  role: string
  isActive: boolean
  authProvider: string
  firstName?: string | null
  lastName?: string | null
  createdAt: string
  hasBusinessProfile: boolean
  businessId?: string | null
  businessName?: string | null
}

export type AdminEntitlementOverride = {
  id: string
  businessId: string
  featureKey: FeatureKey
  boolValue: boolean | null
  intValue: number | null
  expiresAt: string | null
  reason: string | null
  createdAt: string
}

export type AdminInvoiceSummary = {
  id: string
  totalCents: number | null
  currency: string
  status: string
  issuedAt: string
  paidAt: string | null
  hostedInvoiceUrl: string | null
}

export type AdminBusinessDetail = {
  businessId: string
  businessName: string
  businessType: string | null
  location: string | null
  phone: string | null
  createdAt: string

  ownerEmail: string
  ownerName: string | null

  planCode: PlanCode
  planDisplayName: string
  subscriptionStatus: SubscriptionStatus | null
  billingCycle: string | null
  currentPeriodEnd: string | null
  amountCents: number | null
  currency: string | null

  contactCount: number
  optOutCount: number
  serviceCount: number

  nudgeCountsByStatus: Partial<Record<string, number>>

  recentInvoices: AdminInvoiceSummary[]
  overrides: AdminEntitlementOverride[]
}

export type AdminQueueRow = {
  id: string
  businessId: string
  businessName: string
  contactEmail: string
  contactName: string
  status: string
  sequenceNumber: number
  emailSubject: string | null
  sentAt: string | null
  claimedAt: string | null
  replyReceivedAt: string | null
  lastIntent: string | null
  failedStage: string | null
  retryCount: number
  retryAfter: string | null
  createdAt: string
  updatedAt: string
}

export type AdminOverview = {
  kpis: {
    usersTotal: number
    usersNew7d: number
    businessesTotal: number
    businessesNew7d: number
    activeSubscriptions: number
    trialingSubscriptions: number
    mrrCents: number
    currency: string
    sentLast24h: number
    sentPrev24h: number
    failedLast24h: number
    sent7d: number
    recovered7d: number
    recoveryRate7d: number
    staleAiProcessing: number
  }
  last7Days: { day: string; sent: number; recovered: number; failed: number }[]
  activity: {
    kind: 'BUSINESS_SIGNUP' | 'PLAN_CHANGE' | 'NUDGE_FAILED' | 'NUDGE_RECOVERED'
    at: string
    businessId: string
    businessName: string
    detail: string
  }[]
  topBusinesses: {
    businessId: string
    businessName: string
    planCode: PlanCode
    sentLast7d: number
    contactCount: number
  }[]
}

export type AiObservability = {
  queue: {
    depthByStatus: Record<string, number>
    staleAiProcessing: number
    staleThresholdMinutes: number
    failedByStage: Record<string, number>
    sentLast24h: number
    failedLast24h: number
    recentFailures: {
      id: string
      businessId: string
      businessName: string
      failedStage: string | null
      retryCount: number
      retryAfter: string | null
      updatedAt: string
    }[]
    recentSamples: {
      id: string
      businessId: string
      businessName: string
      subject: string
      sentAt: string
    }[]
  }
  ai: {
    available: boolean
    source: string
    claimsByPath: Record<string, number>
    processedByPathOutcome: Record<string, number>
    groundingRejections: Record<string, number>
    lintViolations: Record<string, number>
    personaPopulationRatio: Record<string, number>
    promptCacheHitRatio: Record<string, number>
    intentClassification: Record<string, number>
  }
}

// ─── Pagination ───────────────────────────────────────────────────────────────

export type PaginatedResponse<T> = {
  content: T[]
  // Spring VIA_DTO serialization nests pagination metadata under `page`
  page?: { totalElements: number; totalPages: number; size: number; number: number }
  // Legacy / non-VIA_DTO fallback fields
  totalElements?: number
  totalPages?: number
  number?: number
  size?: number
  first?: boolean
  last?: boolean
}

export function getPageTotal(res: PaginatedResponse<unknown>): number {
  return res.page?.totalElements ?? res.totalElements ?? 0
}

export function getPageCount(res: PaginatedResponse<unknown>): number {
  return res.page?.totalPages ?? res.totalPages ?? 0
}
